import { useState } from 'react'
import { ModalShell, Field, inputClass, CancelButton, PrimaryButton, ServerError } from '../modal-form'
import { useUpsertOperatingDay } from '../../hooks/useScheduleV2'
import { parseIsoDate } from '../../lib/dates'
import type { OperatingDay, Service } from '../../types'

// OperatingDayEditor — spec 15 §5. Toggle a day's running flag, override
// the service count, attach a per-day note.

interface Props {
  eventId: number
  date: string // ISO yyyy-mm-dd
  existing: OperatingDay | undefined
  services: Service[]
  onClose: () => void
}

export function OperatingDayEditor({
  eventId,
  date,
  existing,
  services,
  onClose
}: Props) {
  const upsert = useUpsertOperatingDay(eventId)

  // v5 fallback when no existing row: Thursdays are off by default.
  const fallbackRunning = parseIsoDate(date).getUTCDay() !== 4
  const [running, setRunning] = useState(existing?.running ?? fallbackRunning)
  const [serviceCountRaw, setServiceCountRaw] = useState(
    existing?.serviceCount != null ? String(existing.serviceCount) : ''
  )
  const [note, setNote] = useState(existing?.note ?? '')

  // Today's services = those with date_override null OR date_override === date.
  const todays = services.filter(
    (s) => !s.dateOverride || s.dateOverride === date
  )

  function handleSubmit() {
    const serviceCount =
      serviceCountRaw.trim() === '' ? null : Number(serviceCountRaw)
    if (serviceCount !== null && (!Number.isInteger(serviceCount) || serviceCount < 0)) {
      return
    }
    upsert.mutate(
      { date, body: { running, serviceCount, note } },
      { onSuccess: () => onClose() }
    )
  }

  const d = parseIsoDate(date)
  const title = d.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })

  return (
    <ModalShell
      title={title}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <CancelButton onClick={onClose} disabled={upsert.isPending} />
          <PrimaryButton type="button" onClick={handleSubmit} disabled={upsert.isPending}>
            {upsert.isPending ? 'Saving…' : 'Save day'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <label className="flex items-start gap-3">
            <input
              type="radio"
              checked={running}
              onChange={() => setRunning(true)}
              className="mt-1"
            />
            <div>
              <div className="font-semibold">Running</div>
              <div className="text-xs text-ink-soft">
                {todays.length} service{todays.length === 1 ? '' : 's'} scheduled for this day
              </div>
            </div>
          </label>
          <label className="flex items-start gap-3">
            <input
              type="radio"
              checked={!running}
              onChange={() => setRunning(false)}
              className="mt-1"
            />
            <div>
              <div className="font-semibold">Non-running</div>
              <div className="text-xs text-ink-soft">No public services. Day cell shows oxblood.</div>
            </div>
          </label>

          {running && (
            <Field label="Service count override" hint="Leave blank to inherit from the timetable">
              <input
                type="number"
                min="0"
                step="1"
                value={serviceCountRaw}
                onChange={(e) => setServiceCountRaw(e.target.value)}
                placeholder={String(todays.length)}
                className={`${inputClass(false)} font-mono`}
              />
            </Field>
          )}

          <Field label="Day note">
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder='e.g. "Special charter — gold ticket only"'
              maxLength={400}
              className={inputClass(false)}
            />
          </Field>

          {running && todays.length > 0 && (
            <div className="bg-parchment border border-line rounded p-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-ink-soft mb-2">
                Today's services
              </div>
              <ul className="text-sm space-y-1">
                {todays.map((s) => (
                  <li key={s.id} className="flex items-center gap-3">
                    <span className="font-mono font-semibold w-14">{s.timeText}</span>
                    <span className="flex-1">{s.serviceLabel}</span>
                    <span className="text-ink-soft text-xs">{s.loco ?? '—'}</span>
                    <span className="font-mono text-xs w-16 text-right">
                      {(s.booked ?? 0)}/{s.capacity ?? '—'}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="text-xs text-ink-soft mt-2">
                Edit services on the main Schedule tab.
              </div>
            </div>
          )}

          {upsert.isError && (
            <ServerError title="Could not save day" error={upsert.error as Error} />
          )}
        </div>
      }
    />
  )
}
